import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    MagnifyingGlassIcon,
    DocumentIcon,
    BellIcon
} from '@heroicons/react/24/outline';
import DateTimePicker from './DateTimePicker';
import { useAuth } from '../contexts/AuthContext';

interface InitialFilterValues {
    // Common
    emailSearch?: string;
    dateFrom?: string;
    dateTo?: string;
    
    // Assets
    assetName?: string;
    assetType?: 'file' | 'folder';
    assetTags?: string[];

    // Notifications
    fileName?: string;
    notificationType?: string[];
    notificationStatus?: string[];
}

interface GlobalSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialTab?: SearchType;
    singleTabMode?: boolean; // When true, hide tabs and show only the filters for the current type
    initialFilterValues?: InitialFilterValues; // Current filter values to pre-fill the modal
}

type SearchType = 'assets' | 'notifications';

const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ isOpen, onClose, initialTab, singleTabMode = false, initialFilterValues }) => {
    const { user } = useAuth();
    const [searchType, setSearchType] = useState<SearchType>('assets');

    // Common State - Initialize from initialFilterValues if provided
    const [dateFrom, setDateFrom] = useState<string | null>(initialFilterValues?.dateFrom || null);
    const [dateTo, setDateTo] = useState<string | null>(initialFilterValues?.dateTo || null);
    const [emailSearch, setEmailSearch] = useState(initialFilterValues?.emailSearch || '');

    // Assets State - Initialize from initialFilterValues if provided
    const [assetName, setAssetName] = useState(initialFilterValues?.assetName || '');
    const [assetType, setAssetType] = useState<'file' | 'folder' | undefined>(initialFilterValues?.assetType);
    const [assetTags, setAssetTags] = useState<string[]>(initialFilterValues?.assetTags || []);
    const [tagInput, setTagInput] = useState('');

    // Assets Tab Toggles - Default to only "My assets" selected
    const [selectedAssetTabs, setSelectedAssetTabs] = useState<Set<'my-assets' | 'shared-with-me' | 'requests'>>(
        new Set<'my-assets' | 'shared-with-me' | 'requests'>(['my-assets'])
    );

    // Notifications State - Initialize from initialFilterValues if provided
    const [fileName, setFileName] = useState(initialFilterValues?.fileName || '');
    const [notificationType, setNotificationType] = useState<string[]>(initialFilterValues?.notificationType || []);
    const [notificationStatus, setNotificationStatus] = useState<string[]>(initialFilterValues?.notificationStatus || []);

    // Filter notification types based on user role
    const availableNotificationTypes = useMemo(() => {
        const allTypes = [
            'file_shared',
            'file_access_violation',
            'file_access_revoked',
            'file_access_request',
            'access_count_limit_request',
            'access_period_limit_request',
            'asset_permissions_request',
            'access_request_responded',
            'access_request_canceled',
            'policy_updated',
            'new_device_registered',
            'device_revoked',
            'user_removed_from_group',
            'user_pending_enrollment',
            'infrastructure_provisioning'
        ];
        
        // Remove 'user_pending_enrollment' and 'infrastructure_provisioning' for regular users
        return allTypes.filter(type => {
            if (user?.role === 'user') {
                // Regular users don't see these
                if (type === 'user_pending_enrollment' || type === 'infrastructure_provisioning') {
                    return false;
                }
            }
            return true;
        });
    }, [user?.role]);

    // Initialize tab from props
    useEffect(() => {
        if (isOpen && initialTab) {
            setSearchType(initialTab);
        }
    }, [isOpen, initialTab]);

    // Update state when modal opens with initialFilterValues
    // This preserves current filters when reopening the modal on the same page
    useEffect(() => {
        if (isOpen) {
            if (initialFilterValues) {
                // Only update if we have initialFilterValues (opening from same page with active filters)
                // This preserves the current filters
                if (initialFilterValues.emailSearch !== undefined) setEmailSearch(initialFilterValues.emailSearch);
                if (initialFilterValues.dateFrom !== undefined) setDateFrom(initialFilterValues.dateFrom || null);
                if (initialFilterValues.dateTo !== undefined) setDateTo(initialFilterValues.dateTo || null);
                
                // Update based on current tab
                if (searchType === 'assets') {
                    if (initialFilterValues.assetName !== undefined) setAssetName(initialFilterValues.assetName);
                    if (initialFilterValues.assetType !== undefined) setAssetType(initialFilterValues.assetType);
                    if (initialFilterValues.assetTags !== undefined) setAssetTags(initialFilterValues.assetTags);
                } else if (searchType === 'notifications') {
                    if (initialFilterValues.fileName !== undefined) setFileName(initialFilterValues.fileName);
                    if (initialFilterValues.notificationType !== undefined) setNotificationType(initialFilterValues.notificationType);
                    if (initialFilterValues.notificationStatus !== undefined) setNotificationStatus(initialFilterValues.notificationStatus);
                }
            } else if (!initialTab) {
                // No initialFilterValues and no initialTab -> Clear everything (coming from different page)
                handleClear();
            }
            // If we have initialTab but no initialFilterValues, keep current state (might be from URL params)
        }
    }, [isOpen, initialFilterValues, searchType, initialTab]);

    // Clear specific fields when switching tabs
    useEffect(() => {
        // When switching tabs, we generally want a clean slate for THAT tab
        // unless we are just opening the modal and setting the initial tab.
        // This is a bit tricky. Let's just clear the *other* tabs' data or just reset the current tab's specific data?
        // Simpler: Just clear the type-specific inputs when type changes.
        if (searchType === 'assets') {
            setFileName('');
            setNotificationType([]);
            setNotificationStatus([]);
        } else if (searchType === 'notifications') {
            setAssetName('');
            setAssetType(undefined);
            setAssetTags([]);
        }
    }, [searchType]);


    const handleSearch = () => {
        console.log('🔍 GlobalSearchModal: Starting search for type:', searchType);

        const params = new URLSearchParams();

        // Determine target page based on search type
        let targetPage = '/assets';

        if (searchType === 'assets') {
            targetPage = '/assets';
            if (assetName) params.set('searchTerm', assetName);
            if (emailSearch) params.set('emailSearch', emailSearch);
            if (assetType) params.set('assetType', assetType);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);
            if (assetTags.length > 0) params.set('tags', JSON.stringify(assetTags));
            // Add selected tabs
            if (selectedAssetTabs.size > 0) {
                params.set('selectedTabs', Array.from(selectedAssetTabs).join(','));
            }
        } else if (searchType === 'notifications') {
            targetPage = '/notifications';
            if (emailSearch) params.set('emailSearch', emailSearch);
            if (fileName) params.set('fileName', fileName);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);
            if (notificationType.length > 0) params.set('notificationTypes', JSON.stringify(notificationType));
            if (notificationStatus.length > 0) params.set('status', JSON.stringify(notificationStatus));
        }

        // Navigate to the appropriate page with filters
        const queryString = params.toString();
        window.location.href = queryString ? `${targetPage}?${queryString}` : targetPage;
        onClose();
    };

    const handleClear = () => {
        setAssetName('');
        setEmailSearch('');
        setFileName('');
        setAssetType(undefined);
        setDateFrom(null);
        setDateTo(null);
        setNotificationType([]);
        setNotificationStatus([]);
        setAssetTags([]);
        setTagInput(''); // Clear tag input as well
        setSelectedAssetTabs(new Set<'my-assets' | 'shared-with-me' | 'requests'>(['my-assets'])); // Reset to default
    };

    if (!isOpen) return null;

    const modalRoot = document.getElementById('modal-root') || document.body;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 transition-all duration-300"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-900 w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden transform transition-all scale-100 opacity-100 flex flex-col h-[calc(100vh-10rem)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header / Tabs */}
                {!singleTabMode && (
                    <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <button
                            onClick={() => setSearchType('assets')}
                            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${searchType === 'assets'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50 dark:bg-blue-900/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                                }`}
                        >
                            <DocumentIcon className="w-5 h-5" />
                            Assets
                        </button>
                        <button
                            onClick={() => setSearchType('notifications')}
                            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${searchType === 'notifications'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50 dark:bg-blue-900/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                                }`}
                        >
                            <BellIcon className="w-5 h-5" />
                            Notifications
                        </button>
                    </div>
                )}

                {/* Scrollable Content Area */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">

                    {/* Common Inputs - Always Visible */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Email Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                User / Email
                            </label>
                            <input
                                type="email"
                                value={emailSearch}
                                onChange={(e) => setEmailSearch(e.target.value)}
                                placeholder="user@example.com"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        {/* Date Range */}
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                                <DateTimePicker
                                    value={dateFrom}
                                    onChange={setDateFrom}
                                    placeholder="Start Date"
                                    returnFormat="iso"
                                    className="w-full text-sm"
                                    minDate={null}
                                    maxDate={new Date()}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                                <DateTimePicker
                                    value={dateTo}
                                    onChange={setDateTo}
                                    placeholder="End Date"
                                    returnFormat="iso"
                                    className="w-full text-sm"
                                    minDate={null}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200 dark:border-gray-700" />

                    {/* Type Specific Main Input */}
                    {searchType === 'assets' ? (
                        <>
                            {/* Assets: Asset Name (full width) and Tags (right half) on same row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Asset Name - takes 2/3 of the row */}
                                <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Asset Name
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                            value={assetName}
                                            onChange={(e) => setAssetName(e.target.value)}
                                            placeholder="Search assets by name..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </div>
                                {/* Tags - takes 1/3 of the row */}
                    <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tags
                                    </label>
                                            <input
                                                type="text"
                                                value={tagInput}
                                                onChange={(e) => setTagInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && tagInput.trim()) {
                                                        if (!assetTags.includes(tagInput.trim())) {
                                                            setAssetTags([...assetTags, tagInput.trim()]);
                                                        }
                                                        setTagInput('');
                                                    }
                                                }}
                                                placeholder="Type tag and press Enter"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                            />
                                    {assetTags.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {assetTags.map(tag => (
                                                    <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                        {tag}
                                                        <button
                                                            onClick={() => setAssetTags(assetTags.filter(t => t !== tag))}
                                                            className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Tab Selection Toggles - At the bottom for Assets */}
                            <div className="space-y-3 mt-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Apply filters to:
                            </label>
                            <div className="flex flex-wrap gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <span className="text-sm text-gray-700 dark:text-gray-300">My assets</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newSet = new Set(selectedAssetTabs);
                                            if (selectedAssetTabs.has('my-assets')) {
                                                // Only allow unchecking if there are other tabs selected
                                                if (newSet.size > 1) {
                                                    newSet.delete('my-assets');
                                                }
                                            } else {
                                                newSet.add('my-assets');
                                            }
                                            setSelectedAssetTabs(newSet);
                                        }}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            selectedAssetTabs.has('my-assets')
                                                ? 'bg-blue-600'
                                                : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                selectedAssetTabs.has('my-assets') ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Shared with me</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newSet = new Set(selectedAssetTabs);
                                            if (selectedAssetTabs.has('shared-with-me')) {
                                                // Only allow unchecking if there are other tabs selected
                                                if (newSet.size > 1) {
                                                    newSet.delete('shared-with-me');
                                                }
                                            } else {
                                                newSet.add('shared-with-me');
                                            }
                                            setSelectedAssetTabs(newSet);
                                        }}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            selectedAssetTabs.has('shared-with-me')
                                                ? 'bg-blue-600'
                                                : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                selectedAssetTabs.has('shared-with-me') ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Requests</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newSet = new Set(selectedAssetTabs);
                                            if (selectedAssetTabs.has('requests')) {
                                                // Only allow unchecking if there are other tabs selected
                                                if (newSet.size > 1) {
                                                    newSet.delete('requests');
                                                }
                                            } else {
                                                newSet.add('requests');
                                            }
                                            setSelectedAssetTabs(newSet);
                                        }}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            selectedAssetTabs.has('requests')
                                                ? 'bg-blue-600'
                                                : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                selectedAssetTabs.has('requests') ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </label>
                            </div>
                            {selectedAssetTabs.has('requests') && (
                                <div className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    Applies to both "Received" and "Sent" tabs
                                </div>
                            )}
                                        </div>
                                    </>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                File Name
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={fileName}
                                    onChange={(e) => setFileName(e.target.value)}
                                    placeholder="Search by file name..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Notifications Type and Status - Show directly (not in advanced) */}
                    {searchType === 'notifications' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                                <div className="flex flex-wrap gap-2">
                                    {availableNotificationTypes.map(type => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => {
                                                if (notificationType.includes(type)) {
                                                    setNotificationType(notificationType.filter(t => t !== type));
                                                } else {
                                                    setNotificationType([...notificationType, type]);
                                                }
                                            }}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md border capitalize transition-colors ${notificationType.includes(type)
                                                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                                                }`}
                                        >
                                            {type === 'file_shared' ? 'File Shared' :
                                             type === 'file_access_violation' ? 'Access Violation' :
                                             type === 'file_access_revoked' ? 'Access Revoked' :
                                             type === 'file_access_request' ? 'Access Request' :
                                             type === 'access_count_limit_request' ? 'Count Limit Request' :
                                             type === 'access_period_limit_request' ? 'Period Limit Request' :
                                             type === 'asset_permissions_request' ? 'Permissions Request' :
                                             type === 'access_request_responded' ? 'Request Responded' :
                                             type === 'access_request_canceled' ? 'Request Canceled' :
                                             type === 'policy_updated' ? 'Policy Updated' :
                                             type === 'new_device_registered' ? 'New Device' :
                                             type === 'device_revoked' ? 'Device Revoked' :
                                             type === 'user_removed_from_group' ? 'Removed from Group' :
                                             type === 'user_pending_enrollment' ? 'Pending Enrollment' :
                                             type === 'infrastructure_provisioning' ? 'Infrastructure' :
                                             type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                                <div className="flex flex-wrap gap-2">
                                    {['accepted', 'rejected', 'pending'].map(status => (
                                        <button
                                            key={status}
                                            type="button"
                                            onClick={() => {
                                                if (notificationStatus.includes(status)) {
                                                    setNotificationStatus(notificationStatus.filter(s => s !== status));
                                                } else {
                                                    setNotificationStatus([...notificationStatus, status]);
                                                }
                                            }}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md border capitalize transition-colors ${notificationStatus.includes(status)
                                                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                                                }`}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <button
                        onClick={handleClear}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium"
                    >
                        Clear all filters
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSearch}
                            className="px-6 py-2 text-sm font-medium text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 rounded-lg shadow-sm transition-colors flex items-center gap-2"
                        >
                            <MagnifyingGlassIcon className="w-4 h-4" />
                            Search
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
};

export default GlobalSearchModal;
